import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { AccountOutput, PersonOutput } from '@qtek/shared/models';
import { UserActions } from './user.actions';

interface UserActionParams {
  sts: number;
  vndId: string;
}
interface UserAction {
  params: UserActionParams;
}

export interface UserState {
  prs: PersonOutput;
  acn: AccountOutput | null;
  rel: any | null;
  actions: UserAction[] | null;
}

const initialState: UserState = {
  prs: null,
  acn: null,
  rel: null,
  actions: null,
};

export const userReducer = createReducer(
  initialState,
  on(UserActions.getCurrentUserSuccess, (state, { payload }) =>
    payload == null ? initialState : { ...state, ...payload }
  ),
  on(UserActions.updateCurrentUserSuccess, (state, { payload }) => {
    if (!payload) return state;
    const prevBitMsks = state.prs?.bitMsks;
    const newBitMsks = payload.bitMsks
      ? {
          ...prevBitMsks,
          ...payload.bitMsks,
          _prgs: (prevBitMsks?._prgs ?? 0) | (payload.bitMsks._prgs ?? 0),
        }
      : prevBitMsks;
    return {
      ...state,
      prs: { ...state.prs, ...payload, bitMsks: newBitMsks },
      actions: payload.actions ?? state.actions,
    };
  }),
  on(UserActions.updateCurrentUserCompanySuccess, (state, { payload }) => {
    if (!payload) return state;
    const acn = (payload as any).acn ?? payload;
    if (!acn) return state;
    const prevBitMsks = state.acn?.bitMsks;
    const newBitMsks = acn.bitMsks
      ? {
          ...prevBitMsks,
          ...acn.bitMsks,
          _prgs: (prevBitMsks?._prgs ?? 0) | (acn.bitMsks._prgs ?? 0),
        }
      : prevBitMsks;
    return { ...state, acn: { ...state.acn, ...acn, bitMsks: newBitMsks } };
  })
);

export const UserFeature = createFeature({
  name: 'user',
  reducer: userReducer,
  extraSelectors: ({ selectUserState }) => ({
    isAuthorized: createSelector(
      selectUserState,
      state => state.prs && Boolean(state.prs.prsId)
    ),
    selectCurrentPerson: createSelector(selectUserState, state => state.prs),
    selectMainCompany: createSelector(selectUserState, state => state.acn),
    selectPersonProgress: createSelector(
      selectUserState,
      state => state?.prs?.bitMsks?._prgs || 0
    ),
    selectCompanyProgress: createSelector(
      selectUserState,
      state => state?.acn?.bitMsks?._prgs || 0
    ),
    selectTutorialProgress: createSelector(
      selectUserState,
      state => state?.prs?.bitMsks?._uitut || undefined
    ),
    selectCurrentPersonLanguage: createSelector(
      selectUserState,
      state => state.prs?.lng
    ),
    selectRelationType: createSelector(selectUserState, state =>
      state.rel ? state.rel.rl : null
    ),
    selectUserRelation: createSelector(selectUserState, state => state.rel),
    selectIsPersonalAccount: createSelector(
      selectUserState,
      state => state.acn && (state.acn.tp === 2000 || !state.acn.tp)
    ),
    selectUserActions: createSelector(selectUserState, state => state.actions),
    selectCurrentPersonTz: createSelector(
      selectUserState,
      state => state.prs?.tz
    ),
  }),
});

export const {
  isAuthorized,
  selectCurrentPerson,
  selectMainCompany,
  selectPersonProgress,
  selectCompanyProgress,
  selectTutorialProgress,
  selectCurrentPersonLanguage,
  selectRelationType,
  selectUserRelation,
  selectIsPersonalAccount,
  selectUserActions,
  selectCurrentPersonTz,
} = UserFeature;
